import { useSession } from 'gunsi-auth'
import { BottomSheet } from '../components/BottomSheet.jsx'
import { useEven } from '../hooks/useEven.jsx'
import { useEvenI18n } from '../i18n/even.js'
import { useConfirm } from '../components/ConfirmDialog.jsx'
import { makeMemberLabel, youOf, shortName } from '../util/you.js'
import { memberShareOf, expensePayers } from '../util/balances.js'
import { fmtMoney, fmtDate, formatTime } from '../format.js'

// Dettagli spesa (stile SplitPro): click su una spesa apre QUESTO, non il
// form. Da qui: matita → edit, cestino → delete con conferma.
export function ExpenseDetailsSheet({ open, onClose, group, expense, onEdit }) {
  const t = useEvenI18n()
  const { session } = useSession()
  const { removeExpense, dateFormat } = useEven()
  const { confirm } = useConfirm()

  if (!group || !expense) return null

  const memberLabel = makeMemberLabel(group)
  const you = youOf(group, session?.user?.id)
  const cur = expense.currency || group.currency
  const participants = expense.participants ?? []
  const payers = expensePayers(expense)

  // "Aggiunta da X" se il creatore è un member linkato, altrimenti solo data
  const creator = (group.members ?? []).find(m => m.userId && m.userId === expense.createdBy)
  const edited = expense.updatedAt && expense.createdAt && expense.updatedAt !== expense.createdAt

  async function onDelete() {
    const ok = await confirm({
      message: t.expense.deleteConfirm,
      okLabel: t.common.delete,
      danger: true,
    })
    if (!ok) return
    await removeExpense(expense.id)
    onClose()
  }

  return (
    <BottomSheet open={open} onClose={onClose} className="bsheet-form">
      <form className="form-sheet" onSubmit={e => e.preventDefault()}>
        <header className="form-sheet-head">
          <button type="button" className="btn btn-ghost" onClick={onClose}>{t.common.back}</button>
          <h2>{t.detail.title}</h2>
          <span className="xd-actions">
            <button type="button" className="btn btn-icon xd-delete" onClick={onDelete} aria-label={t.common.delete} title={t.common.delete}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
            <button type="button" className="btn btn-icon" onClick={() => onEdit(expense)} aria-label={t.group.edit} title={t.common.save}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
              </svg>
            </button>
          </span>
        </header>

        <div className="form-sheet-body">
          <div className="xd-hero">
            <div className="xd-emoji">{expense.emoji || '💸'}</div>
            <div className="xd-desc">{expense.description || t.activity.expense}</div>
            <div className="xd-amount">{fmtMoney(expense.amount, cur)}</div>
            <div className="xd-meta">
              {(creator ? t.detail.addedBy.replace('{name}', shortName(creator.name)) : t.detail.addedOn)
                .replace('{date}', fmtDate(expense.createdAt || expense.date, undefined, dateFormat))
                .replace('{time}', formatTime(expense.createdAt || expense.date))}
            </div>
            {edited && (
              <div className="xd-meta">
                {t.detail.editedOn
                  .replace('{date}', fmtDate(expense.updatedAt, undefined, dateFormat))
                  .replace('{time}', formatTime(expense.updatedAt))}
              </div>
            )}
          </div>

          <div className="xd-breakdown">
            {payers.length > 1 ? (
              <div className="xd-payer xd-payer-multi">
                <div className="xd-payer-head">{t.detail.paidByMulti}</div>
                {payers.map(p => (
                  <div key={p.memberId} className="xd-payer-row">
                    <span>{memberLabel(p.memberId)}</span>
                    <strong>
                      {fmtMoney(p.amount, cur)}
                      {/* Anticipato in un'altra valuta: mostriamo quanto gli è
                          stato addebitato davvero, non solo l'equivalente */}
                      {p.origCurrency && p.origCurrency !== cur && (
                        <span className="xd-payer-orig">
                          {' '}({fmtMoney(p.origAmount, p.origCurrency)})
                        </span>
                      )}
                    </strong>
                  </div>
                ))}
              </div>
            ) : (
              <div className="xd-payer">
                <strong>{memberLabel(payers[0]?.memberId)}</strong> {t.group.paid} <strong>{fmtMoney(expense.amount, cur)}</strong>
              </div>
            )}
            <ul className="xd-shares">
              {participants.map(p => {
                const share = memberShareOf(expense, p.memberId)
                if (share <= 0.004) return null
                const isYou = you && p.memberId === you.id
                const isPayer = payers.some(x => x.memberId === p.memberId)
                return (
                  <li key={p.memberId} className={`xd-share-row${isYou && !isPayer ? ' owe' : ''}`}>
                    <span className="xd-share-name">{memberLabel(p.memberId)}</span>
                    <span className="xd-share-verb">{t.detail.owes}</span>
                    <span className="xd-share-amt">{fmtMoney(share, cur)}</span>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </form>
    </BottomSheet>
  )
}
